import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";

interface GuideVideoProps {
	videoUrl: string;
	startTime?: number;
	endTime?: number;
}

const GuideVideo: React.FC<GuideVideoProps> = ({ videoUrl, startTime, endTime }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const [currentTime, setCurrentTime] = useState<number>(0);
	const [isMuted, setIsMuted] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [effectiveStartTime, setEffectiveStartTime] = useState<number>(0);
	const [effectiveEndTime, setEffectiveEndTime] = useState<number>(0);
	const [videoDuration, setVideoDuration] = useState<number>(0);

	// Only use time range if both startTime and endTime are provided
	const useTimeRange = startTime !== undefined && endTime !== undefined;
	const duration: number = useTimeRange ? effectiveEndTime - effectiveStartTime : videoDuration;

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		const handleLoadedMetadata = () => {
			setVideoDuration(video.duration);

			if (useTimeRange) {
				if (startTime > video.duration || endTime > video.duration) {
					console.warn("Time range exceeds video duration. Using full video as fallback.");
					setEffectiveStartTime(0);
					setEffectiveEndTime(video.duration);
					video.currentTime = 0;
				} else {
					setEffectiveStartTime(startTime);
					setEffectiveEndTime(endTime);
					video.currentTime = startTime;
				}
			} else {
				// No time range specified, use full video
				setEffectiveStartTime(0);
				setEffectiveEndTime(video.duration);
				video.currentTime = 0;
			}
			setIsLoading(false);
		};

		const handleTimeUpdate = () => {
			if (useTimeRange) {
				const relativeTime = video.currentTime - effectiveStartTime;
				setCurrentTime(relativeTime);

				// If we reach the end of the range, stop and restart
				if (video.currentTime >= effectiveEndTime) {
					video.currentTime = effectiveStartTime;
					video.pause();
					setIsPlaying(false);
					setCurrentTime(0);
				}

				// If the user backs out before the start, return to the start.
				if (video.currentTime < effectiveStartTime) {
					video.currentTime = effectiveStartTime;
				}
			} else {
				// For full video, use actual current time
				setCurrentTime(video.currentTime);
			}
		};

		const handlePlay = () => setIsPlaying(true);
		const handlePause = () => setIsPlaying(false);

		// Prevent video from going out of range when user tries to navigate (only if using time range)
		const handleSeeking = () => {
			if (useTimeRange) {
				if (video.currentTime < effectiveStartTime) {
					video.currentTime = effectiveStartTime;
				} else if (video.currentTime > effectiveEndTime) {
					video.currentTime = effectiveEndTime;
				}
			}
		};

		video.addEventListener("loadedmetadata", handleLoadedMetadata);
		video.addEventListener("timeupdate", handleTimeUpdate);
		video.addEventListener("play", handlePlay);
		video.addEventListener("pause", handlePause);
		video.addEventListener("seeking", handleSeeking);

		return () => {
			video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			video.removeEventListener("timeupdate", handleTimeUpdate);
			video.removeEventListener("play", handlePlay);
			video.removeEventListener("pause", handlePause);
			video.removeEventListener("seeking", handleSeeking);
		};
	}, [startTime, endTime, effectiveStartTime, effectiveEndTime, useTimeRange]);

	const togglePlay = () => {
		const video = videoRef.current;
		if (!video) return;

		if (isPlaying) {
			video.pause();
		} else {
			if (useTimeRange && video.currentTime >= effectiveEndTime) {
				video.currentTime = effectiveStartTime;
			}
			video.play();
		}
	};

	const toggleMute = () => {
		const video = videoRef.current;
		if (!video) return;
		video.muted = !video.muted;
		setIsMuted(!isMuted);
	};

	const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
		const video = videoRef.current;
		if (!video) return;

		const rect = e.currentTarget.getBoundingClientRect();
		const pos = (e.clientX - rect.left) / rect.width;

		if (useTimeRange) {
			const newTime = effectiveStartTime + pos * duration;
			video.currentTime = newTime;
		} else {
			const newTime = pos * videoDuration;
			video.currentTime = newTime;
		}
	};

	const toggleFullscreen = () => {
		const container = containerRef.current;
		if (!container) return;

		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			container.requestFullscreen();
		}
	};

	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	const progressPercentage = (currentTime / duration) * 100;

	return (
		<div className="guide-video-container" ref={containerRef}>
			<div className="guide-video-wrapper">
				<video
					ref={videoRef}
					src={videoUrl}
					className="guide-video-player"
					onClick={togglePlay}
					controlsList="nodownload nofullscreen noremoteplayback"
					disablePictureInPicture
				/>

				{isLoading && (
					<div className="guide-video-loading">
						<div className="guide-video-loading-text">Loading...</div>
					</div>
				)}
			</div>
			<div className="guide-video-controls">
				<div className="guide-video-progress-container" onClick={handleSeek}>
					<div className="guide-video-progress-bar" style={{ width: `${progressPercentage}%` }} />
				</div>

				<div className="guide-video-controls-wrapper">
					<div className="guide-video-controls-left">
						<button onClick={togglePlay} className="guide-video-button">
							{isPlaying ? <Pause size={24} /> : <Play size={24} />}
						</button>

						<button onClick={toggleMute} className="guide-video-button">
							{isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
						</button>

						<div className="guide-video-time">
							{formatTime(currentTime)} / {formatTime(duration)}
						</div>
					</div>

					<button onClick={toggleFullscreen} className="guide-video-button">
						<Maximize size={24} />
					</button>
				</div>
			</div>
		</div>
	);
};

export default GuideVideo;
